import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { components } from "@dolfin/api-admin";
import { useAuth } from "@/auth/AuthContext";
import { Spinner } from "@/components/ui/spinner";
import { apiErrorMessage, apiErrorType } from "@/lib/api-error";
import { useDemo } from "./DemoContext";
import { useDemoApi } from "./DemoProvider";
import { DEMO_STEPS, PRESET_LABEL, TryDolfinBooks, type TryView } from "./TryDolfinBooks";

type DemoStatus = components["schemas"]["DemoStatusResponse"];

const POLL_MS = 2000;

/** /try: a demo's landing page (the magic link signs in at /handoff, then comes here until the demo is built). */
export function TryDemo() {
  const { isAuthenticated, isLoading } = useAuth();
  const api = useDemoApi();
  const navigate = useNavigate();
  const { refresh } = useDemo();
  const [status, setStatus] = useState<DemoStatus | null>(null);
  const [notDemo, setNotDemo] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [preset, setPreset] = useState<NonNullable<DemoStatus["preset"]> | null>(null);

  const load = useCallback(async () => {
    const { data, error, response } = await api.GET("/v1/demo/status", {});
    if (response.status === 404) setNotDemo(true);
    else if (error || !data) setLoadError(apiErrorMessage(error, "Couldn't load your demo."));
    else {
      setLoadError(null);
      setStatus(data);
    }
  }, [api]);

  useEffect(() => {
    if (isAuthenticated) void load();
  }, [isAuthenticated, load]);

  // Poll while seeding; stop once it settles.
  useEffect(() => {
    if (status?.status !== "Seeding") return;
    const id = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(id);
  }, [status?.status, load]);

  useEffect(() => {
    if (status?.status === "Ready") {
      refresh();
      navigate("/books", { replace: true });
    }
  }, [status?.status, navigate, refresh]);

  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (notDemo) return <Navigate to="/" replace />;

  async function start() {
    setStarting(true);
    // The body only matters for a demo without a preset; the server ignores it otherwise.
    const { data, error } = await api.POST("/v1/demo/seed", { body: preset ? { preset } : null });
    setStarting(false);
    if (error || !data) {
      if (apiErrorType(error).includes("Demo.AlreadySeeded")) {
        navigate("/books", { replace: true });
        return;
      }
      toast.error(apiErrorMessage(error, "Couldn't start the demo."));
      return;
    }
    setStatus(data);
  }

  if (!status) {
    return (
      <main className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4">
        {loadError ? (
          <div className="max-w-sm text-center">
            <p className="text-sm text-destructive">{loadError}</p>
            <button type="button" className="mt-3 text-sm font-medium underline underline-offset-4" onClick={() => void load()}>
              Try again
            </button>
          </div>
        ) : (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner className="size-4" /> Loading your demo
          </p>
        )}
      </main>
    );
  }

  const chosen = status.preset ?? preset;
  const view: TryView = {
    status: status.status ?? "NotStarted",
    company: status.companyName,
    presetLabel: chosen ? PRESET_LABEL[chosen] : null,
    needsPreset: !status.preset,
    startDate: status.startDate ?? null,
    expiresAt: status.expiresAt ?? null,
    steps: status.steps.length
      ? status.steps.map((s) => ({ key: s.key ?? s.label, label: s.label, state: s.state ?? "Pending" }))
      : DEMO_STEPS,
    error: status.error ?? null,
  };
  return (
    <TryDolfinBooks
      view={view}
      starting={starting}
      onTry={() => void start()}
      preset={preset}
      onPreset={setPreset}
    />
  );
}
